import { useEffect, useMemo, useState } from "react";
import axiosAdmin from "../axiosAdmin";
import { ConfirmModal } from "../components/ConfirmModal";
import { StatusBadge } from "../components/StatusBadge";
import { 
  Plus, 
  Store, 
  Edit, 
  Lock, 
  Power, 
  Trash2, 
  Upload, 
  X,
  UserPlus
} from "lucide-react";

const PAGE_SIZE = 10; // Garder la pagination même si elle n'est pas encore implémentée dans la logique UI

export default function AdminMerchants() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // modal suspend/reactive
  const [confirmMerchant, setConfirmMerchant] = useState(null);

  // modal create
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    adminNote: "",
  });

  // modal edit
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editMerchant, setEditMerchant] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });

  // modal reset password
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMerchant, setResetMerchant] = useState(null);
  const [resetting, setResetting] = useState(false);

  // logo upload
  const [uploadingLogoId, setUploadingLogoId] = useState(null);

  // SUPPRESSION DEFINITIVE
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [merchantToDelete, setMerchantToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Pagination (simplifiée pour l'instant)
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(PAGE_SIZE);

  const resetForm = () => {
    setForm({ email: "", name: "", password: "", adminNote: "" });
  };

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axiosAdmin.get("/admin/merchants");
      setMerchants(res.data.merchants || []);
    } catch (err) {
      console.error("❌ AdminMerchants:", err);
      setError("Impossible de charger les marchands");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
  }, []);

  // ✅ HELPER INTELLIGENT POUR LE LOGO (Cloudinary vs Local)
  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path; // URL Cloudinary
    return `https://souqi-api.onrender.com${path.startsWith("/") ? "" : "/"}${path}`; // URL Local
  };

  // =========================
  // LOGO UPLOAD
  // =========================
  const uploadLogo = async (merchantId, file) => {
    if (!merchantId || !file) return;

    try {
      setUploadingLogoId(merchantId);

      const fd = new FormData();
      fd.append("logo", file);

      const res = await axiosAdmin.patch(`/admin/merchants/${merchantId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ✅ On utilise le chemin renvoyé par le backend (Cloudinary ou local)
      const newPath = res.data.logo;

      setMerchants(prev => prev.map(m => 
        m._id === merchantId ? { ...m, logo: newPath } : m
      ));

    } catch (err) {
      console.error("❌ uploadLogo:", err);
      alert(err.response?.data?.error || "Erreur upload logo");
    } finally {
      setUploadingLogoId(null);
    }
  };

  // =========================
  // TOGGLE MARCHAND
  // =========================
  const toggleMerchant = async (m) => {
    try {
      await axiosAdmin.patch(`/admin/merchants/${m._id}/toggle`);
      setConfirmMerchant(null);
      fetchMerchants();
    } catch (err) {
      console.error("❌ toggleMerchant:", err);
      alert("Erreur lors de l'action");
    }
  };

  // =========================
  // CREATION
  // =========================
  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;

    try {
      setCreating(true);

      await axiosAdmin.post("/admin/merchants", {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        adminNote: form.adminNote.trim(),
      });

      setCreateOpen(false);
      resetForm();
      fetchMerchants();
    } catch (err) {
      console.error("❌ createMerchant:", err);
      alert(err.response?.data?.error || "Erreur création marchand");
    } finally {
      setCreating(false);
    }
  };  
  
  // =========================
  // EDIT
  // =========================
  const openEdit = (m) => {
    setEditMerchant(m);
    setEditForm({
      name: m.name || "",
      email: m.email || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editMerchant) return;

    try {
      setEditing(true);
      await axiosAdmin.patch(`/admin/merchants/${editMerchant._id}`, {
        name: editForm.name,
        email: editForm.email,
      });

      setEditOpen(false);
      setEditMerchant(null);
      fetchMerchants();
    } catch (err) {
      console.error("❌ updateMerchant:", err);
      alert(err.response?.data?.error || "Erreur modification marchand");
    } finally {
      setEditing(false);
    }
  };

  // =========================
  // RESET PASSWORD
  // =========================
  const confirmResetPassword = (m) => {
    setResetMerchant(m);
    setResetOpen(true);
  };

  const doResetPassword = async () => {
    if (!resetMerchant) return;
    try {
      setResetting(true);

      const res = await axiosAdmin.post(
        `/admin/merchants/${resetMerchant._id}/reset-password`,
        {}
      );

      setResetOpen(false);

      const temp = res.data?.tempPassword;
      alert(
        temp
          ? `Mot de passe réinitialisé ✅\n\nNouveau mot de passe (à communiquer au marchand) : ${temp}\n\n⚠️ Il ne sera pas ré-affiché.`
          : "Mot de passe réinitialisé ✅"
      );

      setResetMerchant(null);
    } catch (err) {
      console.error("❌ resetMerchantPassword:", err);
      alert(err.response?.data?.error || "Erreur reset mot de passe");
    } finally {
      setResetting(false);
    }
  };

  // =========================
  // SUPPRESSION DEFINITIVE
  // =========================
  const confirmDeleteMerchant = (m) => {
    setMerchantToDelete(m);
    setDeleteOpen(true);
  };

  const doDeleteMerchant = async () => {
    if (!merchantToDelete) return;

    try {
      setDeleting(true);
      await axiosAdmin.delete(
        `/admin/merchants/${merchantToDelete._id}`
      );
      setDeleteOpen(false);
      setMerchantToDelete(null);
      fetchMerchants();
    } catch (err) {
      console.error("❌ deleteMerchant:", err);
      alert(err.response?.data?.error || "Erreur suppression marchand");
    } finally {
      setDeleting(false);
    }
  };

  // ------------------------------------------------------------------
  // RENDU
  // ------------------------------------------------------------------

  const rows = useMemo(() => {
    return merchants;
  }, [merchants]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh] text-gray-400">
        Chargement des marchands...
    </div>
  );
  
  if (error) return (
    <div className="p-8 m-8 text-center text-red-500 border border-red-100 bg-red-50 rounded-xl">
        {error}
    </div>
  );

  return (
    <div className="min-h-screen p-8 space-y-8 font-sans bg-gray-50/50">
      
      {/* 1. EN-TÊTE DE SECTION */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
            <h1 className="flex items-center text-3xl font-extrabold tracking-tight text-gray-900">
                <Store className="mr-3 text-gray-400" size={28} />
                Gestion des Marchands
            </h1>
            <p className="mt-1 text-sm text-gray-500">Vue d'ensemble et administration des comptes marchands.</p>
        </div>

        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center px-4 py-2.5 text-sm font-bold text-white bg-[#f34141] rounded-xl shadow-md hover:bg-red-600 transition-colors"
        >
          <UserPlus size={16} className="mr-2" />
          Ajouter un marchand
        </button>
      </div>

      {/* 2. TABLEAU DE DONNÉES (CARD STYLE) */}
      <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs font-semibold tracking-wider text-gray-500 uppercase border-b border-gray-100 bg-gray-50/80">
              <tr>
                <th className="px-6 py-4">Logo</th>
                <th className="px-6 py-4">Marchand & Note</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4 text-center">Commandes</th>
                <th className="px-6 py-4">CA Marchand</th>
                <th className="px-6 py-4">Dernière commande</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {rows.map((m) => (
                <tr key={m._id} className="transition-colors hover:bg-gray-50">
                  {/* LOGO & UPLOAD - CORRIGÉ AVEC getLogoUrl */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {m.logo ? (
                        <img
                          src={getLogoUrl(m.logo)}
                          alt={m.name || "Logo"}
                          className="object-contain w-10 h-10 bg-white border border-gray-200 rounded-lg"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-10 h-10 text-gray-400 bg-gray-100 border border-gray-200 rounded-lg">
                          <Store size={16} />
                        </div>
                      )}

                      <label 
                        className="flex items-center text-xs font-semibold text-blue-600 transition-colors cursor-pointer hover:text-blue-700"
                        title="Télécharger un nouveau logo"
                      >
                        {uploadingLogoId === m._id ? (
                          <>
                            <Upload size={14} className="mr-1 animate-pulse" />
                            Upload...
                          </>
                        ) : (
                          <>
                            <Upload size={14} className="mr-1" />
                            Changer
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) =>
                            uploadLogo(m._id, e.target.files?.[0])
                          }
                        />
                      </label>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900">
                      {m.name || "Marchand sans nom"}
                    </div>
                    <div className="text-xs italic text-gray-500 mt-0.5">
                      {m.adminNote || "Aucune note administrative"}
                    </div>
                  </td>

                  <td className="px-6 py-4 font-mono text-xs text-gray-600">{m.email}</td>

                  <td className="px-6 py-4">
                    <StatusBadge
                      active={!!m.isActive}
                      activeLabel="Actif"
                      inactiveLabel="Suspendu"
                    />
                  </td>

                  <td className="px-6 py-4 font-bold text-center text-gray-700">
                    {m.ordersCount ?? 0}
                  </td>

                  <td className="px-6 py-4 font-bold text-gray-900">
                    {m.revenueSubtotal ?? 0} <span className="text-xs font-normal text-gray-400">MRU</span>
                  </td>

                  <td className="px-6 py-4 text-sm text-gray-500">
                    {m.lastOrderAt
                      ? new Date(m.lastOrderAt).toLocaleDateString()
                      : "—"}
                  </td>

                  <td className="px-6 py-4 space-x-2 text-right">
                    
                    <button
                      onClick={() => openEdit(m)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Edit size={14} className="mr-1.5" />
                      Modifier
                    </button>

                    <button
                      onClick={() => confirmResetPassword(m)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-orange-700 bg-orange-100 border border-orange-200 rounded-lg hover:bg-orange-200 transition-colors"
                    >
                      <Lock size={14} className="mr-1.5" />
                      Réinit. MDP
                    </button>

                    <button
                      onClick={() => setConfirmMerchant(m)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-bold border rounded-lg transition-all ${
                        m.isActive
                          ? "bg-red-100 text-red-700 border-red-200 hover:bg-red-200"
                          : "bg-green-100 text-green-700 border-green-200 hover:bg-green-200"
                      }`}
                    >
                      <Power size={14} className="mr-1.5"/>
                      {m.isActive ? "Suspendre" : "Réactiver"}
                    </button>

                    <button
                      onClick={() => confirmDeleteMerchant(m)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-white bg-gray-800 border border-gray-700 rounded-lg hover:bg-black transition-colors"
                    >
                      <Trash2 size={14} className="mr-1.5"/>
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-400">
                Aucun marchand trouvé.
            </div>
          )}
        </div>
      </div>     

      {/* MODALES - TOUTES CONSERVÉES AVEC TON DESIGN EXACT */}
      <ConfirmModal
        open={deleteOpen}
        title="Zone de Danger : Suppression définitive"
        description="Cette action est irréversible. Le marchand, ses produits et son logo seront supprimés définitivement. Continuer ?"
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setMerchantToDelete(null);
        }}
        onConfirm={doDeleteMerchant}
        confirmLabel={deleting ? "Suppression…" : "Supprimer définitivement"}
        confirmClass="bg-red-600 hover:bg-red-700"
      />

      <ConfirmModal
        open={!!confirmMerchant}
        title="Confirmation de statut"
        description={`Souhaitez-vous vraiment ${confirmMerchant?.isActive ? "SUSPENDRE" : "RÉACTIVER"} ce marchand ?`}
        onCancel={() => setConfirmMerchant(null)}
        onConfirm={() => toggleMerchant(confirmMerchant)}
        confirmLabel={
          confirmMerchant?.isActive ? "Suspendre" : "Réactiver"
        }
        confirmClass={confirmMerchant?.isActive ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
      />

      <ConfirmModal
        open={resetOpen}
        title="Réinitialiser le mot de passe"
        description="Cette action génère un nouveau mot de passe et remplace l’ancien. Confirmer ?"
        onCancel={() => {
          if (resetting) return;
          setResetOpen(false);
          setResetMerchant(null);
        }}
        onConfirm={doResetPassword}
        confirmLabel={resetting ? "Réinitialisation…" : "Confirmer la réinitialisation"}
        confirmClass="bg-orange-500 hover:bg-orange-600"
      />

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 px-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="w-full max-w-lg p-6 bg-white shadow-2xl rounded-2xl">
            <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center text-xl font-extrabold text-gray-800">
                    <Edit size={20} className="mr-2 text-gray-500"/>
                    Modifier {editMerchant?.name || "Marchand"}
                </h2>
                <button 
                    onClick={() => {if (editing) return; setEditOpen(false); setEditMerchant(null);}} 
                    className="p-2 text-gray-400 transition rounded-full hover:bg-gray-100 hover:text-gray-600"
                >
                    <X size={20} />
                </button>
            </div>
            <form onSubmit={submitEdit} className="space-y-4">
              <input
                className="w-full px-4 py-3 transition border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                placeholder="Nom"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
              <input
                className="w-full px-4 py-3 transition border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                placeholder="Email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
              <div className="flex justify-end gap-3 pt-2 mt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { if (editing) return; setEditOpen(false); setEditMerchant(null); }}
                  className="px-5 py-2 font-bold text-gray-700 transition bg-gray-100 rounded-xl hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={editing || !editForm.email}
                  className="px-5 py-2 font-bold text-white transition bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50"
                >
                  {editing ? "Sauvegarde…" : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 px-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="w-full max-w-lg p-6 bg-white shadow-2xl rounded-2xl">
            <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center text-xl font-extrabold text-gray-800">
                    <UserPlus size={20} className="mr-2 text-gray-500"/>
                    Ajouter un marchand
                </h2>
                <button 
                    onClick={() => {if (creating) return; setCreateOpen(false); resetForm();}} 
                    className="p-2 text-gray-400 transition rounded-full hover:bg-gray-100 hover:text-gray-600"
                >
                    <X size={20} />
                </button>
            </div>
            <form onSubmit={submitCreate} className="space-y-4">
              <input
                className="w-full px-4 py-3 transition border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <input
                className="w-full px-4 py-3 transition border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                placeholder="Nom (optionnel)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                type="password"
                className="w-full px-4 py-3 transition border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                placeholder="Mot de passe initial"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
              <textarea
                className="w-full px-4 py-3 transition border border-gray-300 resize-none rounded-xl focus:ring-blue-500 focus:border-blue-500"
                placeholder="Note admin (optionnel)"
                rows={3}
                value={form.adminNote}
                onChange={(e) => setForm({ ...form, adminNote: e.target.value })}
              />
              <div className="flex justify-end gap-3 pt-2 mt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setCreateOpen(false); resetForm(); }}
                  className="px-5 py-2 font-bold text-gray-700 transition bg-gray-100 rounded-xl hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creating || !form.email || !form.password}
                  className="px-5 py-2 font-bold text-white bg-[#f34141] rounded-xl hover:bg-red-600 disabled:opacity-50 transition"
                >
                  {creating ? "Création…" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}